import { useEffect, useState, useMemo } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { AlertTriangle, ListChecks, Filter, Search } from "lucide-react";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";

const STATUS_OPTS = ["", "Pending", "Partially Returned", "Closed"];

export default function ReturnableRegisterPage() {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterMember, setFilterMember] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    const p = new URLSearchParams();
    if (filterBranch) p.append("branch_id", filterBranch);
    if (filterMember) p.append("member_id", filterMember);
    if (filterStatus) p.append("status", filterStatus);
    const { data } = await api.get(`/returnable-register?${p}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBranch, filterMember, filterStatus]);
  useEffect(() => { api.get("/members/_pickable").then(r => setMembers(r.data)); }, []);

  const filtered = useMemo(() => {
    let base = overdueOnly ? rows.filter(r => r.overdue) : rows;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(r => [
      r.issue_voucher_number, r.item_name, r.item_code, r.batch_code, r.asset_code,
      r.issued_to_name, r.line_type, r.status, r.branch_name, r.expected_return_date,
    ].some(x => (x || "").toString().toLowerCase().includes(q)));
  }, [rows, overdueOnly, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    pending: rows.filter(r => r.status === "Pending").length,
    partial: rows.filter(r => r.status === "Partially Returned").length,
    closed: rows.filter(r => r.status === "Closed").length,
    overdue: rows.filter(r => r.overdue).length,
  }), [rows]);

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(filtered, { initialPageSize: 50 });

  return (
    <div>
      <PageHeader
        eyebrow="Stores"
        title="Returnable Register"
        subtitle="All items issued as Returnable — track pending returns and overdue items."
      />

      {/* Filters */}
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="rr-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by voucher / item / batch / asset / member…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[320px]" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 flex items-center gap-1"><Filter size={11} /> Filters</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="rr-branch-filter" />
        <select data-testid="rr-member-filter" value={filterMember} onChange={e => setFilterMember(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">All members</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select data-testid="rr-status-filter" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input data-testid="rr-overdue-toggle" type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} className="accent-[#F97316]" />
          Overdue only
        </label>
      </div>

      {/* Stats tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatTile label="Total" value={stats.total} testid="stat-total" />
        <StatTile label="Pending" value={stats.pending} tone="amber" testid="stat-pending" />
        <StatTile label="Partially Returned" value={stats.partial} tone="blue" testid="stat-partial" />
        <StatTile label="Closed" value={stats.closed} tone="emerald" testid="stat-closed" />
        <StatTile label="Overdue" value={stats.overdue} tone="red" testid="stat-overdue" />
      </div>

      {filtered.length === 0 ? <EmptyState title="No returnable items" icon={ListChecks} /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rr-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Issue No", "Type", "Item / Asset", "Issued To", "Branch", "Issued", "Returned", "Pending", "Exp. Return", "Status"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {paged.map(r => (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${r.overdue ? "bg-red-50/30" : ""}`}>
                    <td className="py-3 px-3 text-xs font-mono font-bold">{r.issue_voucher_number}</td>
                    <td className="py-3 px-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${r.line_type === "Material" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>{r.line_type}</span></td>
                    <td className="py-3 px-3">
                      {r.line_type === "Material"
                        ? <>{r.item_name}<div className="text-[10px] text-slate-400 font-mono">{r.batch_code}</div></>
                        : <><span className="font-mono text-xs font-bold">{r.asset_code}</span><div className="text-[10px] text-slate-500">{r.item_name}</div></>}
                    </td>
                    <td className="py-3 px-3 text-xs text-slate-700">
                      {r.issued_to_name || "—"}
                      {r.issued_to_type && <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mt-0.5">{r.issued_to_type}</div>}
                    </td>
                    <td className="py-3 px-3 text-xs text-slate-500">{r.branch_name}</td>
                    <td className="py-3 px-3 text-xs tabular">{r.qty_issued} {r.unit}</td>
                    <td className="py-3 px-3 text-xs tabular">{r.qty_returned} {r.unit}</td>
                    <td className="py-3 px-3 text-xs tabular font-bold">{r.qty_pending} {r.unit}</td>
                    <td className={`py-3 px-3 text-xs ${r.overdue ? "text-red-700 font-bold" : "text-slate-500"}`}>
                      {r.expected_return_date || "—"}
                      {r.overdue && <AlertTriangle size={11} className="inline ml-1" />}
                    </td>
                    <td className="py-3 px-3"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        </Card>
      )}
    </div>
  );
}

function StatTile({ label, value, tone = "slate", testid }) {
  const palette = {
    slate: "border-slate-200 bg-white", amber: "border-amber-200 bg-amber-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40", blue: "border-blue-200 bg-blue-50/40",
    red: "border-red-200 bg-red-50/40",
  }[tone];
  const tcol = {
    slate: "text-slate-900", amber: "text-amber-800", emerald: "text-emerald-800",
    blue: "text-blue-800", red: "text-red-700",
  }[tone];
  return (
    <div className={`border ${palette} px-4 py-3`} data-testid={testid || `stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">{label}</div>
      <div className={`mt-1 text-2xl font-display font-bold tabular ${tcol}`}>{value}</div>
    </div>
  );
}
